/**
 * QuickRepliesSettings — respostas rápidas.
 * Mobile: toolbar Contatos-like. Desktop intacto.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { useQuickRepliesPage } from './hooks/useQuickRepliesPage';
import QuickReplyTable from './components/QuickReplyTable';
import QuickReplyFormModal from './components/QuickReplyFormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import ActionMenu from '../../../components/shared/ActionMenu';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import BulkActionsBar from '../../../components/shared/BulkActionsBar';
import Pagination from '../../../components/shared/Pagination';

const QuickRepliesSettings: React.FC = () => {
    const {
        filteredReplies,
        loading,
        searchTerm,
        setSearchTerm,
        selectedIds,
        replyToEdit,
        replyToDelete,
        isFormModalOpen,
        setIsFormModalOpen,
        isDeleteModalOpen,
        setIsDeleteModalOpen,
        isBulkDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveReply,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        headerActions,
        bulkActions,
    } = useQuickRepliesPage();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedReplies = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        return filteredReplies.slice(firstPageIndex, firstPageIndex + pageSize);
    }, [currentPage, filteredReplies, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Respostas Rápidas"
                subtitle="Crie atalhos para mensagens frequentes."
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                actionClassName="w-full md:w-auto"
                hideBackButton
                action={
                    <>
                        <div className="flex w-full flex-col gap-s3 md:hidden">
                            <div
                                role="toolbar"
                                aria-label="Ações de respostas rápidas"
                                className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card"
                            >
                                <ActionMenu
                                    actions={headerActions}
                                    buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                                />
                            </div>
                            <SearchInput
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Pesquisar respostas..."
                                className="min-h-11 w-full"
                            />
                            <Button
                                type="button"
                                onClick={handleOpenCreateModal}
                                className="min-h-11 w-full touch-manipulation"
                            >
                                Nova Resposta
                            </Button>
                        </div>
                        <div className="hidden md:flex md:items-center md:gap-2.5">
                            <div className="w-[250px] shrink-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar respostas..."
                                    className="h-[36px]"
                                />
                            </div>
                            <Button type="button" onClick={handleOpenCreateModal}>
                                Nova Resposta
                            </Button>
                            <ActionMenu actions={headerActions} />
                        </div>
                    </>
                }
            />

            <section aria-label="Lista de respostas rápidas" className="overflow-x-auto pt-s3 md:pt-0">
                <QuickReplyTable
                    replies={paginatedReplies}
                    isLoading={loading}
                    onEdit={handleOpenEditModal}
                    onDelete={handleOpenDeleteModal}
                    onDuplicate={handleDuplicate}
                    selectedIds={selectedIds}
                    onToggleSelection={toggleSelection}
                    onToggleAll={() => toggleAllSelection(filteredReplies)}
                />
            </section>
            <Pagination
                className="mt-s3"
                currentPage={currentPage}
                totalCount={filteredReplies.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                selectedCount={selectedIds.size}
            />

            <QuickReplyFormModal
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSaveReply}
                replyToEdit={replyToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Excluir Resposta Rápida"
                message={`Tem certeza que deseja excluir a resposta "${replyToDelete?.shortcut}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Respostas Rápidas"
                message={`Tem certeza que deseja excluir ${selectedIds.size} resposta(s)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />
        </div>
    );
};

export default QuickRepliesSettings;
